<template>
  <div class="f-main bgImg container">
    <div class="box" :style="wrapperStyle">
      <div class="title">
        <div class="title-top">
          <div class="title-left">
            <div class="title-badges">
              <span class="badge badge-subject">
                <i class="el-icon-reading" />
                <span>{{ heroSubject }}</span>
              </span>
              <span v-if="heroLevel" class="badge badge-level">{{ heroLevel }}</span>
            </div>
            <div class="title-main">
              <i class="title-icon el-icon-notebook-2" aria-hidden="true" />
              <div class="title-text">
                <div class="title-name">{{ $t("pointLearn.title") }}</div>
                <div class="title-sub">{{ heroSubtitle }}</div>
              </div>
            </div>
          </div>

          <div class="title-right">
            <div v-if="heroProgress" class="title-stats">
              <div class="stat-pill">
                <div class="stat-num">{{ heroProgress.lessons }}</div>
                <div class="stat-label">Lessons</div>
              </div>
              <div class="stat-pill">
                <div class="stat-num">{{ heroProgress.done }}</div>
                <div class="stat-label">Done</div>
              </div>
            </div>
            <button class="btn-home" type="button" @click="toHome">
              <i class="el-icon-house" />
              <span>{{ $t("studentHomepage.home") }}</span>
            </button>
          </div>
        </div>

        <div class="title-bottom">
          <div class="crumbs">
            <span class="crumb">
              <i class="el-icon-menu" />
              <span>{{ $t("studentHomepage.home") }}</span>
            </span>
            <span class="sep">›</span>
            <span class="crumb">{{ $t("studentHomepage.myLearningPlan") }}</span>
            <span v-if="heroPoint" class="sep">›</span>
            <span v-if="heroPoint" class="crumb crumb-strong">{{ heroPoint }}</span>
          </div>
        </div>
      </div>
      <div class="content">
        <div class="left">
          <div class="content-display">
            <!-- 内容展示区域 -->
            <div ref="contentContainer" class="content-container">
              <div v-if="courseId" class="lesson-content-toolbar">
                <span v-if="historyData && historyData.versionNo" class="current-version">
                  {{ $t('pointLearn.currentVersion', { version: historyData.versionNo }) }}
                </span>
                <button
                  type="button"
                  class="history-trigger"
                  :disabled="regenerationLoading"
                  @click="openCourseHistory"
                >
                  <i class="el-icon-time" aria-hidden="true" />
                  {{ $t('pointLearn.versionHistory') }}
                </button>
              </div>
              <section v-if="regenerationLoading" class="regeneration-progress" role="status">
                <div class="regeneration-progress-title">
                  <i class="el-icon-loading" aria-hidden="true" />
                  <span>{{ $t('pointLearn.regenerationGenerating') }}</span>
                </div>
                <div v-if="regenerationPreview" class="regeneration-preview lesson-prose">
                  <div v-html="formatMessage(regenerationPreview)"></div>
                </div>
              </section>
              <div v-if="historyData" class="historyData lesson-prose">
                <div v-html="formatMessage(historyData.content.explanation)"></div>
                <div v-html="formatMessage(historyData.content.examples)"></div>
                <div v-html="formatMessage(historyData.content.keySummary)"></div>
                <div v-html="formatMessage(historyData.content.additionalInfo)"></div>
              </div>
              <div v-if="rawContentList.length" class="rawContentList lesson-prose">
                <div v-for="(item, ind) in rawContentList" :key="ind" >
                  <div v-html="formatMessage(item.rawContent)"></div>
                </div>
              </div>
              <button
                v-if="showCompleteStage && !learningCompleted"
                type="button"
                class="complete-stage"
                :disabled="stageAdvanceLoading || regenerationLoading || !courseId"
                @click="completeCurrentStage"
              >
                <i v-if="stageAdvanceLoading" class="el-icon-loading" aria-hidden="true" />
                {{ $t(stageAdvanceLoading ? 'pointLearn.completingStage' : 'pointLearn.completeCurrentStage') }}
              </button>
              <div v-if="learningCompleted" class="learning-completed" role="status">
                <i class="el-icon-circle-check" aria-hidden="true" />
                <div>
                  <h3>{{ $t('pointLearn.allStagesCompletedTitle') }}</h3>
                  <p>{{ $t('pointLearn.allStagesCompletedBody') }}</p>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div class="right">
          <div class="chat-hero">
            <div class="chat-hero-left">
              <div class="chat-hero-icon">
                <i class="el-icon-s-custom" />
              </div>
              <div class="chat-hero-text">
                <div class="chat-hero-title">AI Tutor</div>
                <div class="chat-hero-sub">Online & ready to help</div>
              </div>
            </div>
            <div class="chat-hero-right">
              <button type="button" class="chat-hero-action" @click="openChatHistory">
                <i class="el-icon-time" aria-hidden="true" />
                <span>{{ $t('chat.history.button') }}</span>
              </button>
              <button type="button" class="chat-hero-action chat-hero-action--primary" @click="startNewChat">
                <i class="el-icon-plus" aria-hidden="true" />
                <span>{{ $t('chat.history.newChat') }}</span>
              </button>
            </div>
          </div>
          <Chat
            ref="learningChat"
            :learn="true"
            :learning-context="learningContext"
            :learning-context-loading="learningContextLoading"
            :learning-context-error="learningContextError"
            :student-name="learningStudentName"
            :knowledge-point-id="$route.query.knowledgePointId"
          />
        </div>
      </div>
    </div>
    <div class="footer">{{ $t("common.footer") }}</div>
    <el-drawer
      :visible.sync="historyDrawerVisible"
      :title="$t('pointLearn.historyTitle')"
      direction="rtl"
      size="520px"
      append-to-body
      @closed="resetCourseHistory"
    >
      <div class="course-history-panel">
        <template v-if="selectedHistorySummary">
          <button type="button" class="history-back" @click="backToHistoryList">
            <i class="el-icon-back" aria-hidden="true" />
            {{ $t('pointLearn.backToHistory') }}
          </button>

          <div class="history-detail-heading">
            <div>
              <strong>
                {{ $t('pointLearn.versionLabel', { version: selectedHistorySummary.versionNo }) }}
              </strong>
              <span>{{ formatHistoryDate(selectedHistorySummary.archivedAt) }}</span>
            </div>
            <span class="history-readonly">{{ $t('pointLearn.historyReadonly') }}</span>
          </div>

          <div v-if="historyDetailLoading" class="history-state">
            <i class="el-icon-loading" aria-hidden="true" />
            <span>{{ $t('pointLearn.historyLoading') }}</span>
          </div>
          <div v-else-if="historyDetailError" class="history-state history-state--error">
            <span>{{ $t('pointLearn.historyDetailFailed') }}</span>
            <button type="button" @click="openHistoryVersion(selectedHistorySummary)">
              {{ $t('pointLearn.retry') }}
            </button>
          </div>
          <div v-else-if="historyDetailSections.length" class="history-snapshot lesson-prose">
            <div
              v-for="(section, sectionIndex) in historyDetailSections"
              :key="sectionIndex"
              v-html="formatMessage(section)"
            ></div>
          </div>
          <div v-else class="history-state">
            {{ $t('pointLearn.historyContentEmpty') }}
          </div>
        </template>

        <template v-else>
          <div v-if="historyListLoading" class="history-state">
            <i class="el-icon-loading" aria-hidden="true" />
            <span>{{ $t('pointLearn.historyLoading') }}</span>
          </div>
          <div v-else-if="historyListError" class="history-state history-state--error">
            <span>{{ $t('pointLearn.historyLoadFailed') }}</span>
            <button type="button" @click="loadCourseHistory(historyPageNum)">
              {{ $t('pointLearn.retry') }}
            </button>
          </div>
          <div v-else-if="!historyList.length" class="history-state">
            <i class="el-icon-document" aria-hidden="true" />
            <span>{{ $t('pointLearn.historyEmpty') }}</span>
          </div>
          <template v-else>
            <div class="history-list">
              <button
                v-for="item in historyList"
                :key="item.id"
                type="button"
                class="history-list-item"
                @click="openHistoryVersion(item)"
              >
                <div class="history-list-item-top">
                  <strong>{{ $t('pointLearn.versionLabel', { version: item.versionNo }) }}</strong>
                  <span>{{ formatHistoryDate(item.archivedAt) }}</span>
                </div>
                <div class="history-list-item-bottom">
                  <span>
                    {{ $t('pointLearn.historyStageProgress', { completed: item.completedStage || 0 }) }}
                  </span>
                  <span class="history-view">
                    {{ $t('pointLearn.viewVersion') }}
                    <i class="el-icon-arrow-right" aria-hidden="true" />
                  </span>
                </div>
              </button>
            </div>
            <el-pagination
              v-if="historyTotal > historyPageSize"
              class="history-pagination"
              small
              layout="prev, pager, next"
              :current-page="historyPageNum"
              :page-size="historyPageSize"
              :total="historyTotal"
              @current-change="loadCourseHistory"
            />
          </template>
        </template>
      </div>
    </el-drawer>
  </div>
</template>

<script>
import axios from 'axios'
import Chat from '@/views/Chat.vue'
import {
  completeStage,
  detail,
  getCourseHistory,
  getCourseHistoryDetail,
  getLearningContext,
  regenerateCourseStream,
  stream
} from '@/api/tutor/learn'
import { queryWithProgress } from '@/api/tutor/student'
import { appendCourseStreamText } from '@/utils/courseRegeneration.mjs'
import { handleSessionExpired } from '@/utils/http'
import { buildLearningContextKey, isLearningContextData } from '@/utils/learningContext.mjs'
import { mapState } from 'vuex'
// 引入KaTeX和marked依赖
import { marked } from 'marked'
import 'katex/dist/katex.min.css'
import katex from 'katex'

export default {
  name: 'pointLearn',
  components: { Chat },
  props: {
    scale: {
      type: Number,
      required: false, // 修正：required应该是false，默认值用default
      default: 1
    }
  },
  computed: {
    ...mapState('user', {
      loggedInUser: (state) => state.userInfo
    }),
    wrapperStyle () {
      return {
        transform: `scale(${this.scale})`
      }
    },
    /**
     * 顶部横幅：学科/知识点信息（尽量从路由参数读取，避免依赖接口字段）
     * @returns {string}
     */
    heroSubject () {
      const q = this.$route?.query || {}
      const v = q.categoryName || q.subjectName || q.category || q.subject
      return v ? String(v) : 'Number & Algebra'
    },
    /**
     * 顶部横幅：难度标签
     * @returns {string}
     */
    heroLevel () {
      const d = String(this.$route?.query?.difficultyLevel || '').trim()
      if (!d) return ''
      if (d === '1') return this.$t('studentHomepage.Basic')
      if (d === '2') return this.$t('studentHomepage.Intermediate')
      if (d === '3') return this.$t('studentHomepage.Advanced')
      return ''
    },
    /**
     * 顶部横幅：副标题（知识点/小节）
     * @returns {string}
     */
    heroSubtitle () {
      const q = this.$route?.query || {}
      const v = q.pointName || q.knowledgePointName || q.stageName || q.unitName
      return v ? String(v) : 'Online & ready to help'
    },
    /**
     * 顶部横幅：面包屑当前点
     * @returns {string}
     */
    heroPoint () {
      const q = this.$route?.query || {}
      const v = q.pointName || q.knowledgePointName || q.unitName
      return v ? String(v) : ''
    },
    /**
     * 顶部横幅：进度（若接口有值则展示；否则不显示）
     * @returns {{lessons: string, done: string}|null}
     */
    heroProgress () {
      const v = this.historyData
      if (!v) return null
      const lessons = v.lessonProgress || v.lessons || v.stageProgress
      const done = v.donePercent || v.done || v.progress
      if (!lessons && lessons !== 0) return null
      if (!done && done !== 0) return null
      return {
        lessons: String(lessons),
        done: typeof done === 'number' ? `${Math.round(done)}%` : String(done)
      }
    },
    learningContextKey () {
      return buildLearningContextKey({
        studentId: this.$route.query.userId,
        knowledgePointId: this.$route.query.knowledgePointId,
        language: this.$i18n.locale === 'fr' ? 'fr' : 'en'
      })
    },
    learningStudentName () {
      const q = this.$route.query || {}
      const routeName = q.studentName || q.childName
      if (routeName) return String(routeName)

      const user = this.loggedInUser || JSON.parse(localStorage.getItem('userInfo') || 'null')
      if (user && String(user.id) === String(q.userId)) {
        return String(user.username || user.userAccount || '')
      }
      return ''
    },
    historyDetailSections () {
      const content = this.historyDetail && this.historyDetail.content
      if (!content) return []
      return [
        content.explanation,
        content.examples,
        content.keySummary,
        content.additionalInfo
      ].filter(Boolean)
    }
  },
  data () {
    return {
      // 原始接口返回内容
      rawContent: '',
      index: 0,
      // 格式化后的内容数组
      rawContentList: [],
      courseId: false,
      historyData: null,
      showCompleteStage: true,
      stageAdvanceLoading: false,
      learningCompleted: false,
      learningContext: null,
      learningContextLoading: true,
      learningContextError: false,
      learningContextRequestId: 0,
      regenerationLoading: false,
      regenerationPreview: '',
      regenerationCancelSource: null,
      regenerationPromptHandled: false,
      regenerationInfo: null,
      historyDrawerVisible: false,
      historyListLoading: false,
      historyListError: false,
      historyList: [],
      historyTotal: 0,
      historyPageNum: 1,
      historyPageSize: 20,
      selectedHistorySummary: null,
      historyDetail: null,
      historyDetailLoading: false,
      historyDetailError: false
    }
  },
  watch: {
    learningContextKey (nextKey, previousKey) {
      if (previousKey && nextKey !== previousKey) {
        this.regenerationPromptHandled = false
        this.init()
      }
    }
  },
  mounted () {
    this.init()
  },
  beforeDestroy () {
    ++this.learningContextRequestId
    if (this.regenerationCancelSource) {
      this.regenerationCancelSource.cancel('Course page closed')
      this.regenerationCancelSource = null
    }
  },
  methods: {
    openChatHistory () {
      const chat = this.$refs.learningChat
      if (chat) chat.openLearningHistory()
    },
    startNewChat () {
      const chat = this.$refs.learningChat
      if (chat) chat.startNewLearningChat()
    },
    async init () {
      const detailPromise = this.loadCourseDetail()
      this.loadLearningContext()
      await detailPromise
      await this.confirmRouteRegeneration()
    },
    async loadCourseDetail ({ generateIfMissing = true } = {}) {
      this.learningCompleted = false
      this.showCompleteStage = true
      const params = {
        knowledgePointId: this.$route.query.knowledgePointId,
        studentId: this.$route.query.userId,
        language: this.$i18n.locale !== 'en' ? 'fr' : 'en'
      }
      try {
        const res = await detail(params)
        const course = res && res.code === 200 ? res.data || null : null
        if (!course && generateIfMissing) {
          this.historyData = null
          this.courseId = false
          await this.getNewContent()
          return null
        }
        if (!course) return null

        this.historyData = course
        this.courseId = course.id
        if (course.content) {
          if (course.completedStage != null) {
            const courseCompleted = Number(course.completedStage) >= 4
            this.learningCompleted = courseCompleted
            this.showCompleteStage = !courseCompleted
          }
        }
        return course
      } catch (error) {
        return null
      }
    },
    async confirmRouteRegeneration () {
      if (
        this.$route.query.regenerate !== '1' ||
        this.regenerationPromptHandled
      ) {
        return
      }

      this.regenerationPromptHandled = true
      if (!this.courseId) {
        this.clearRegenerationIntent()
        this.$message.error(this.$t('pointLearn.regenerationUnavailable'))
        return
      }
      if (this.historyData && this.historyData.generationStatus === 'GENERATING') {
        this.clearRegenerationIntent()
        this.$message.warning(this.$t('pointLearn.regenerationInProgress'))
        return
      }

      try {
        await this.$confirm(
          this.$t('pointLearn.regenerationConfirmBody'),
          this.$t('pointLearn.regenerationConfirmTitle'),
          {
            confirmButtonText: this.$t('pointLearn.regenerationConfirmButton'),
            cancelButtonText: this.$t('pointLearn.Cancel'),
            type: 'warning',
            closeOnClickModal: false
          }
        )
      } catch (error) {
        this.clearRegenerationIntent()
        return
      }

      await this.regenerateCurrentCourse()
    },
    clearRegenerationIntent () {
      const query = { ...this.$route.query }
      delete query.regenerate
      delete query.sourceTestRecordId
      this.$router.replace({
        path: this.$route.path,
        query
      }).catch(() => {})
    },
    async regenerateCurrentCourse () {
      if (this.regenerationLoading || !this.courseId) return

      this.regenerationLoading = true
      this.regenerationPreview = ''
      const cancelSource = axios.CancelToken.source()
      this.regenerationCancelSource = cancelSource

      try {
        const regeneratedCourse = await regenerateCourseStream({
          courseId: this.courseId,
          sourceTestRecordId: this.$route.query.sourceTestRecordId || null,
          language: this.$i18n.locale === 'fr' ? 'fr' : 'en',
          cancelToken: cancelSource.token,
          onContent: (content) => {
            this.regenerationPreview = appendCourseStreamText(
              this.regenerationPreview,
              content
            )
          }
        })

        if (!regeneratedCourse || !regeneratedCourse.id) {
          const error = new Error('Invalid regenerated course response')
          error.code = 'REGENERATION_GENERATION_FAILED'
          throw error
        }

        this.historyData = regeneratedCourse
        this.courseId = regeneratedCourse.id
        this.rawContentList = []
        this.learningCompleted = false
        this.showCompleteStage = true

        await this.loadCourseDetail({ generateIfMissing: false })
        await Promise.all([
          this.loadLearningContext(),
          this.refreshRegenerationEligibility()
        ])
        if (this.historyDrawerVisible) {
          await this.loadCourseHistory(1)
        }
        this.$nextTick(() => {
          if (this.$refs.contentContainer) {
            this.$refs.contentContainer.scrollTop = 0
          }
        })
        this.clearRegenerationIntent()
        this.$message.success(this.$t('pointLearn.regenerationSuccess'))
      } catch (error) {
        if (!axios.isCancel(error)) {
          this.clearRegenerationIntent()
          this.handleRegenerationError(error)
        }
      } finally {
        if (this.regenerationCancelSource === cancelSource) {
          this.regenerationCancelSource = null
        }
        this.regenerationLoading = false
        this.regenerationPreview = ''
      }
    },
    async refreshRegenerationEligibility () {
      const params = {
        studentId: this.$route.query.userId,
        gradeId: this.$route.query.gradeId,
        categoryId: this.$route.query.categoryId,
        knowledgePointId: this.$route.query.knowledgePointId
      }
      try {
        const res = await queryWithProgress(params)
        if (res.code !== 200 || !Array.isArray(res.data)) return
        const current = res.data.find(item =>
          String(item.id) === String(this.$route.query.knowledgePointId)
        )
        this.regenerationInfo = current ? current.regeneration || null : null
      } catch (error) {
        // The new course is already ready; a list refresh failure is non-blocking.
      }
    },
    openCourseHistory () {
      if (!this.courseId || this.regenerationLoading) return
      this.historyDrawerVisible = true
      this.backToHistoryList()
      this.loadCourseHistory(1)
    },
    async loadCourseHistory (pageNum = 1) {
      const params = {
        studentId: this.$route.query.userId,
        knowledgePointId: this.$route.query.knowledgePointId,
        difficultyLevel: this.$route.query.difficultyLevel || undefined,
        pageNum,
        pageSize: this.historyPageSize
      }
      if (!params.studentId || !params.knowledgePointId) {
        this.historyListError = true
        return
      }

      this.historyListLoading = true
      this.historyListError = false
      try {
        const res = await getCourseHistory(params)
        if (!res || res.code !== 200 || !res.data) {
          throw new Error('Course history request failed')
        }
        this.historyList = Array.isArray(res.data.list) ? res.data.list : []
        this.historyTotal = Number(res.data.total) || 0
        this.historyPageNum = Number(res.data.pageNum) || pageNum
      } catch (error) {
        this.historyListError = true
      } finally {
        this.historyListLoading = false
      }
    },
    async openHistoryVersion (item) {
      if (!item || !item.id || this.historyDetailLoading) return
      this.selectedHistorySummary = item
      this.historyDetail = null
      this.historyDetailLoading = true
      this.historyDetailError = false
      try {
        const res = await getCourseHistoryDetail({
          historyId: item.id,
          language: this.$i18n.locale === 'fr' ? 'fr' : 'en'
        })
        if (!res || res.code !== 200 || !res.data) {
          throw new Error('Course history detail request failed')
        }
        this.historyDetail = res.data
      } catch (error) {
        this.historyDetailError = true
      } finally {
        this.historyDetailLoading = false
      }
    },
    backToHistoryList () {
      this.selectedHistorySummary = null
      this.historyDetail = null
      this.historyDetailLoading = false
      this.historyDetailError = false
    },
    resetCourseHistory () {
      this.backToHistoryList()
      this.historyListError = false
    },
    formatHistoryDate (value) {
      return value ? String(value) : this.$t('pointLearn.historyDateUnknown')
    },
    handleRegenerationError (error) {
      const code = error && error.code
      if (code === 'UNAUTHORIZED' || error?.response?.status === 401) {
        handleSessionExpired()
        return
      }

      const noAssessmentCodes = [
        'NO_NEW_ASSESSMENT',
        'REGENERATION_ALREADY_USED'
      ]
      const unavailableCodes = [
        'COURSE_NOT_FOUND',
        'COURSE_NOT_OWNED',
        'COURSE_NOT_COMPLETED',
        'ASSESSMENT_NOT_COMPLETED',
        'ASSESSMENT_HAS_NO_VALID_ANSWER',
        'ASSESSMENT_DOES_NOT_COVER_TOPIC',
        'INVALID_LANGUAGE'
      ]

      if (noAssessmentCodes.includes(code)) {
        this.$message.warning(this.$t('pointLearn.regenerationNoAssessment'))
      } else if (code === 'ASSESSMENT_REPORT_NOT_READY') {
        this.$message.warning(this.$t('pointLearn.regenerationReportPending'))
      } else if (code === 'REGENERATION_IN_PROGRESS') {
        this.$message.warning(this.$t('pointLearn.regenerationInProgress'))
      } else if (unavailableCodes.includes(code)) {
        this.$message.error(this.$t('pointLearn.regenerationUnavailable'))
      } else {
        this.$message.error(this.$t('pointLearn.regenerationFailed'))
      }
    },
    async loadLearningContext () {
      const requestId = ++this.learningContextRequestId
      const params = {
        studentId: this.$route.query.userId,
        knowledgePointId: this.$route.query.knowledgePointId,
        language: this.$i18n.locale === 'fr' ? 'fr' : 'en'
      }

      this.learningContext = null
      this.learningContextLoading = true
      this.learningContextError = false

      if (!params.studentId || !params.knowledgePointId) {
        this.learningContextLoading = false
        this.learningContextError = true
        return
      }

      try {
        const res = await getLearningContext(params)
        if (requestId !== this.learningContextRequestId) return
        if (res.code === 200 && isLearningContextData(res.data)) {
          this.learningContext = res.data
          const courseStageKnown = this.historyData?.completedStage != null
          const courseCompleted = Number(this.historyData?.completedStage) >= 4
          if (res.data.status === 'COMPLETED' && (!courseStageKnown || courseCompleted)) {
            this.learningCompleted = true
            this.showCompleteStage = false
          } else if (courseStageKnown && !courseCompleted) {
            this.learningCompleted = false
            this.showCompleteStage = true
          }
        } else {
          this.learningContextError = true
        }
      } catch (error) {
        if (requestId === this.learningContextRequestId) {
          this.learningContextError = true
        }
      } finally {
        if (requestId === this.learningContextRequestId) {
          this.learningContextLoading = false
        }
      }
    },
    // 增强版公式处理方法，支持KaTeX渲染
    formatMessage (content) {
      if (!content) return ''
      let text = content

      // 处理行内公式（同时支持$...$和\(...\)格式）
      const processInlineFormula = (formula) => {
        try {
          // 只做最基本的清理
          formula = formula.trim()

          return katex.renderToString(formula, {
            displayMode: false,
            throwOnError: false,
            strict: false
          })
        } catch (error) {
          console.error('KaTeX渲染错误:', error)
          console.error('公式内容:', formula)
          return formula
        }
      }

      // 先处理公式再交给 marked；支持跨行（s），并归一化流式可能插入的空格
      const normalizeFormula = (s) => s.trim().replace(/\s+/g, ' ')
      // $...$ 行内公式
      text = text.replace(/\$(.*?)\$/gs, (_, formula) =>
        processInlineFormula(normalizeFormula(formula))
      )
      // \(...\) 行内公式（要求反斜杠，避免误匹配普通括号）
      text = text.replace(/\\\((.*?)\\\)/gs, (_, formula) =>
        processInlineFormula(normalizeFormula(formula))
      )
      // 块级 \[...\]
      text = text.replace(/\\\[(.*?)\\\]/gs, (_, formula) => {
        try {
          const normalized = normalizeFormula(formula)
          return katex.renderToString(normalized, {
            displayMode: true,
            throwOnError: false,
            strict: false
          })
        } catch (error) {
          console.error('KaTeX渲染错误:', error)
          return formula
        }
      })

      return marked(text)
    },

    async completeCurrentStage () {
      if (this.stageAdvanceLoading || this.regenerationLoading || !this.courseId) return
      this.stageAdvanceLoading = true

      try {
        const res = await completeStage({ id: this.courseId })
        if (res.code === 200) {
          const completedStage = Number(res.data && res.data.completedStage)
          if (Number.isFinite(completedStage) && this.historyData) {
            this.$set(this.historyData, 'completedStage', completedStage)
          }
          if (completedStage >= 4) {
            this.markLearningCompleted()
            return
          }
          await this.getNewContent()
          return
        }
        if (res.message === '当前阶段已完成，没有新的阶段需要标记完成') {
          this.markLearningCompleted()
          return
        }
        this.$message.error(this.$t('pointLearn.completeStageFailed'))
      } catch (error) {
        this.$message.error(this.$t('pointLearn.completeStageFailed'))
      } finally {
        this.stageAdvanceLoading = false
      }
    },
    markLearningCompleted () {
      this.learningCompleted = true
      this.showCompleteStage = false
      this.loadLearningContext()
    },
    /**
     * 请求新内容（流式接口）
     * 流式按词/片段推送；空 data: 视为换行。用 completedRaw + lineBuffer 每 chunk 更新展示，实现实时渲染。
     */
    getNewContent () {
      this.stageAdvanceLoading = true
      const params = {
        knowledgePointId: this.$route.query.knowledgePointId,
        studentId: this.$route.query.userId,
        difficultyLevel: this.$route.query.difficultyLevel,
        language: this.$i18n.locale === 'fr' ? 'fr' : 'en'
      }
      const newContentItem = { rawContent: '' }
      this.rawContentList.push(newContentItem)
      const length = this.rawContentList.length - 1

      /** 按后端 delta.content 原样累积，避免前端猜测词间空格破坏正文。 */
      let streamedRaw = ''
      /** 是否收到阶段正文或阶段详情 JSON；完全没有内容表示全部阶段已完成 */
      let receivedContent = false

      const updateDisplay = () => {
        this.$set(this.rawContentList[length], 'rawContent', streamedRaw)
      }

      return stream(params, (chunk) => {
        if (typeof chunk !== 'string') return
        const trimmed = (chunk && chunk.trim()) || ''

        // 最后一个阶段完成后，后端可能返回 [NO_DATA] 或完全不返回正文。
        if (trimmed === '[NO_DATA]') return

        if (chunk.startsWith('{') && chunk.endsWith('}')) {
          try {
            updateDisplay()
            this.historyData = JSON.parse(chunk)
            receivedContent = true
            this.showCompleteStage = true
            this.rawContentList = []
            this.courseId = this.historyData.id
            return
          } catch (e) {
            this.showCompleteStage = true
            console.error('JSON 解析失败:', e)
          }
        }

        // Spring SSE 的空 data 代表换行；其他 token 必须与后端入库逻辑一样原样拼接。
        streamedRaw += chunk === '' ? '\n' : chunk
        receivedContent = true
        updateDisplay()
      }).then(() => {
        if (!receivedContent) {
          const emptyItemIndex = this.rawContentList.indexOf(newContentItem)
          if (emptyItemIndex >= 0) this.rawContentList.splice(emptyItemIndex, 1)
          this.markLearningCompleted()
        }
      }).catch(() => {
        const emptyItemIndex = this.rawContentList.indexOf(newContentItem)
        if (emptyItemIndex >= 0 && !newContentItem.rawContent.trim()) {
          this.rawContentList.splice(emptyItemIndex, 1)
        }
        this.showCompleteStage = true
        this.$message.error(this.$t('pointLearn.completeStageFailed'))
      }).finally(() => {
        this.stageAdvanceLoading = false
      })
    },
    // 跳转到首页方法（补充实现）
    toHome () {
      this.$router.go(-1)
    }
  }
}
</script>

<style scoped>
.container {
  --lp-orange: #ff8a00;
}

.container {
  display: flex;
  justify-content: center;
  align-items: center;
  height: calc(100% - 90px);
  overflow-y: hidden;

  .footer {
    height: 12px;
    font-weight: 400;
    font-size: 12px;
    color: #afafaf;
    position: absolute;
    bottom: 20px;
  }

  .box {
    width: 1443px;
    max-height: calc(100% - 360px);
    min-height: 850px;
    display: flex;
    flex-direction: column;

    .title {
      margin: 16px 20px 0;
      min-height: 140px;
      background: linear-gradient(90deg, #5b21b6 0%, #6d28d9 45%, #7c3aed 100%);
      border-radius: 14px;
      display: flex;
      flex-direction: column;
      justify-content: space-between;
      color: #ffffff;
      position: relative;
      overflow: hidden;
      flex-shrink: 0;

      &::before{
        content: "";
        position: absolute;
        left: -30%;
        top: -40%;
        width: 520px;
        height: 520px;
        background: radial-gradient(circle at 30% 30%, rgba(255,255,255,0.25) 0%, rgba(255,255,255,0) 62%);
        pointer-events: none;
      }

      .title-top{
        display: flex;
        align-items: center;
        justify-content: space-between;
        gap: 16px;
        z-index: 1;
        padding: 16px 24px 0;
      }

      .title-left{
        min-width: 0;
        display: flex;
        flex-direction: column;
        gap: 10px;
      }

      .title-badges{
        display: flex;
        gap: 10px;
        align-items: center;
        flex-wrap: wrap;
      }

      .badge{
        display: inline-flex;
        align-items: center;
        gap: 8px;
        height: 24px;
        padding: 0 10px;
        border-radius: 999px;
        font-weight: 700;
        font-size: 12px;
        background: rgba(255,255,255,0.18);
        color: rgba(255,255,255,0.95);
        border: 1px solid rgba(255,255,255,0.18);
        i{ font-size: 13px; }
      }
      .badge-level{
        background: rgba(255,255,255,0.12);
      }

      .title-main{
        display: flex;
        align-items: center;
        gap: 12px;
        min-width: 0;
      }

      .title-icon{
        width: 40px;
        height: 40px;
        border-radius: 12px;
        display: flex;
        align-items: center;
        justify-content: center;
        background: rgba(255,255,255,0.18);
        border: 1px solid rgba(255,255,255,0.18);
        font-size: 20px;
        flex-shrink: 0;
      }

      .title-text{
        min-width: 0;
      }

      .title-name{
        font-weight: 800;
        font-size: 26px;
        line-height: 1.15;
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
      }

      .title-sub{
        margin-top: 4px;
        font-size: 16px;
        color: rgba(255,255,255,0.82);
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
      }

      .title-right{
        display: flex;
        align-items: center;
        gap: 12px;
        flex-shrink: 0;
      }

      .title-stats{
        display: flex;
        gap: 10px;
        align-items: center;
      }

      .stat-pill{
        width: 66px;
        height: 44px;
        border-radius: 12px;
        background: rgba(255,255,255,0.16);
        border: 1px solid rgba(255,255,255,0.18);
        display: flex;
        flex-direction: column;
        justify-content: center;
        align-items: center;
        line-height: 1.1;
      }
      .stat-num{
        font-weight: 900;
        font-size: 16px;
      }
      .stat-label{
        margin-top: 3px;
        font-size: 11px;
        color: rgba(255,255,255,0.78);
      }

      .btn-home {
        display: inline-flex;
        align-items: center;
        gap: 8px;
        padding: 16px 26px;
        border: none;
        border-radius: 999px;
        background: var(--lp-orange);
        color: #fff;
        font-size: 18px;
        font-weight: 700;
        cursor: pointer;
        box-shadow: 0 4px 16px rgba(255, 138, 0, 0.45);
        transition: transform 0.2s, box-shadow 0.2s;
      }

      .btn-home:hover {
        transform: translateY(-1px);
        box-shadow: 0 6px 20px rgba(255, 138, 0, 0.5);
      }

      .title-bottom{
        margin-top: 12px;
        padding: 10px 12px;
        background: rgba(255,255,255,0.12);
        border: 1px solid rgba(255,255,255,0.14);
        display: flex;
        align-items: center;
        justify-content: space-between;
        gap: 12px;
        z-index: 1;
      }

      .crumbs{
        display: flex;
        align-items: center;
        gap: 10px;
        min-width: 0;
        color: rgba(255,255,255,0.86);
        font-size: 16px;
        white-space: nowrap;
        overflow: hidden;
      }
      .crumb{
        display: inline-flex;
        align-items: center;
        gap: 6px;
        min-width: 0;
        span{
          overflow: hidden;
          text-overflow: ellipsis;
        }
        i{ font-size: 12px; opacity: .9; }
      }
      .crumb-strong{
        color: rgba(255,255,255,0.96);
        font-weight: 800;
      }
      .sep{
        opacity: .6;
        flex-shrink: 0;
      }

      .nav{
        display: flex;
        align-items: center;
        gap: 10px;
        flex-shrink: 0;
      }
      .nav-btn{
        height: 30px;
        padding: 0 12px;
        border-radius: 999px;
        border: 1px solid rgba(255,255,255,0.22);
        background: rgba(255,255,255,0.12);
        color: rgba(255,255,255,0.92);
        display: inline-flex;
        align-items: center;
        gap: 8px;
        font-weight: 700;
        font-size: 12px;
        cursor: not-allowed;
        opacity: 0.85;
      }
      .nav-btn-primary{
        background: rgba(255,255,255,0.18);
      }
    }

    .content {
      height: calc(100% - 100px);
      display: flex;
      padding: 24px 0;
      flex: 1;
      overflow: hidden;

      .left,
      .right {
        width: calc(50% - 40px);
        max-height: 100%;
        background: #ffffff;
        box-shadow: 0px 0px 10px 1px rgba(0, 0, 0, 0.15);
        border-radius: 24px;
        margin: 0 20px;
      }

      .right{
        overflow: hidden;
        display: flex;
        flex-direction: column;
        .home-container{
          height: calc(100% - 80px);
          max-height: calc(100% - 80px);
        }
        .chat-hero{
          height: 80px;
          border-radius: 24px 24px 0 0 ;
          background: linear-gradient(90deg, #5b21b6 0%, #6d28d9 45%, #7c3aed 100%);
          display: flex;
          align-items: center;
          justify-content: space-between;
          padding: 0 14px;
          color: #fff;
          box-sizing: border-box;
          box-shadow: 0 14px 30px rgba(91, 33, 182, 0.22);
          flex-shrink: 0;
        }

        .chat-hero-left{
          display: flex;
          align-items: center;
          gap: 10px;
          min-width: 0;
        }

        .chat-hero-icon{
          width: 48px;
          height: 48px;
          border-radius: 10px;
          background: rgba(255,255,255,0.18);
          border: 1px solid rgba(255,255,255,0.18);
          display: flex;
          align-items: center;
          justify-content: center;
          flex-shrink: 0;
          i{
            font-size: 16px;
            opacity: 0.95;
          }
        }

        .chat-hero-text{
          min-width: 0;
        }

        .chat-hero-title{
          font-weight: 900;
          font-size: 22px;
          line-height: 1.1;
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
        }

        .chat-hero-sub{
          margin-top: 3px;
          font-size: 18px;
          color: rgba(255,255,255,0.82);
          line-height: 1.1;
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
        }

        .chat-hero-right{
          display: flex;
          align-items: center;
          gap: 8px;
          flex-shrink: 0;
        }

        .chat-hero-action{
          display: inline-flex;
          align-items: center;
          gap: 8px;
          height: 36px;
          padding: 0 12px;
          border-radius: 999px;
          background: rgba(255,255,255,0.16);
          border: 1px solid rgba(255,255,255,0.18);
          font-size: 14px;
          font-weight: 700;
          color: rgba(255,255,255,0.92);
          cursor: pointer;
          transition: background 0.2s ease, transform 0.2s ease;
          i{
            font-size: 16px;
            opacity: 0.95;
          }
        }

        .chat-hero-action:hover{
          background: rgba(255,255,255,0.24);
          transform: translateY(-1px);
        }

        .chat-hero-action--primary{
          background: rgba(139, 92, 246, 0.92);
          border-color: rgba(255,255,255,0.28);
        }

        /* 让 Chat 本体填满剩余高度（不侵入 Chat 组件内部） */
        :deep(.chat),
        :deep(.chat-wrapper),
        :deep(.chat-container){
          flex: 1;
          min-height: 0;
        }
      }

      .left {
        padding: 30px 0 ;
        display: flex;
        flex-direction: column;
        overflow: hidden; /* 防止内容溢出父容器 */

        .content-display {
          flex: 1;
          margin: 0 auto;
          line-height: 1.6;
          color: #333;
          overflow: hidden; /* 隐藏溢出，让子元素滚动 */
          max-width: 100%;
          border-radius: 10px;
        }

        .content-container {
          height: 100%;
          background: #fff;
          padding: 0 30px;
          border-radius: 8px;
          box-shadow: 0 2px 10px rgba(0, 0, 0, 0.1);
          overflow-y: auto; /* 开启垂直滚动 */
          box-sizing: border-box; /* 确保 padding 不撑开高度 */
        }

        .lesson-content-toolbar {
          position: sticky;
          top: 0;
          z-index: 2;
          display: flex;
          align-items: center;
          justify-content: flex-end;
          gap: 10px;
          padding: 0 0 12px;
          background: #fff;
        }

        .current-version {
          font-size: 12px;
          font-weight: 700;
          color: #64748b;
        }

        .history-trigger {
          display: inline-flex;
          align-items: center;
          gap: 6px;
          min-height: 34px;
          padding: 0 13px;
          border: 1px solid #ddd6fe;
          border-radius: 999px;
          background: #f5f3ff;
          color: #5b21b6;
          font-size: 13px;
          font-weight: 700;
          cursor: pointer;
        }

        .history-trigger:hover:not(:disabled) {
          border-color: #a78bfa;
          background: #ede9fe;
        }

        .history-trigger:disabled {
          cursor: not-allowed;
          opacity: 0.55;
        }

        .regeneration-progress {
          margin: 0 0 24px;
          padding: 18px 20px;
          border: 1px solid #ddd6fe;
          border-radius: 14px;
          background: linear-gradient(135deg, #faf5ff 0%, #f5f3ff 100%);
          color: #4c1d95;
        }

        .regeneration-progress-title {
          display: flex;
          align-items: center;
          gap: 9px;
          font-size: 15px;
          font-weight: 700;
        }

        .regeneration-preview {
          max-height: 240px;
          margin-top: 14px;
          padding-top: 12px;
          border-top: 1px solid #e9d5ff;
          overflow-y: auto;
          color: #334155;
        }

        .complete-stage {
          display: flex;
          align-items: center;
          gap: 8px;
          height: 36px;
          padding: 0 12px;
          border: 0;
          border-radius: 999px;
          font-size: 15px;
          background: var(--lp-orange);
          color: #fff;
          font-weight: 600;
          cursor: pointer;
          text-align: center;
          min-width: 190px;
          justify-content: center;
          margin: 0 auto;
        }
        .complete-stage:disabled {
          cursor: wait;
          opacity: 0.72;
        }
        .learning-completed {
          display: flex;
          align-items: flex-start;
          gap: 12px;
          margin: 18px 0 4px;
          padding: 16px;
          border: 1px solid #bbf7d0;
          border-radius: 14px;
          color: #166534;
          background: #f0fdf4;
        }
        .learning-completed > i {
          margin-top: 2px;
          font-size: 24px;
        }
        .learning-completed h3 {
          margin: 0 0 4px;
          font-size: 16px;
        }
        .learning-completed p {
          margin: 0;
          font-size: 14px;
          line-height: 1.5;
        }
        /* KaTeX公式样式优化 */

        :deep(.katex) {
          font-size: 1.1em;
        }

        :deep(.katex-display) {
          margin: 1em 0;
          overflow: auto hidden;
        }

      }
    }
  }
}

.course-history-panel {
  height: 100%;
  padding: 0 24px 24px;
  box-sizing: border-box;
  overflow-y: auto;
  color: #1e293b;
}

.history-state {
  min-height: 220px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 12px;
  color: #64748b;
  font-size: 14px;
  text-align: center;
}

.history-state > i {
  font-size: 28px;
  color: #7c3aed;
}

.history-state button,
.history-back {
  border: 0;
  background: transparent;
  color: #6d28d9;
  font-weight: 700;
  cursor: pointer;
}

.history-state--error {
  color: #b91c1c;
}

.history-list {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.history-list-item {
  width: 100%;
  padding: 16px;
  border: 1px solid #e2e8f0;
  border-radius: 14px;
  background: #fff;
  color: inherit;
  text-align: left;
  cursor: pointer;
  transition: border-color 0.2s, box-shadow 0.2s, transform 0.2s;
}

.history-list-item:hover {
  border-color: #c4b5fd;
  box-shadow: 0 8px 20px rgba(91, 33, 182, 0.08);
  transform: translateY(-1px);
}

.history-list-item-top,
.history-list-item-bottom {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
}

.history-list-item-top strong {
  color: #4c1d95;
  font-size: 16px;
}

.history-list-item-top span,
.history-list-item-bottom {
  color: #64748b;
  font-size: 12px;
}

.history-list-item-bottom {
  margin-top: 10px;
  padding-top: 10px;
  border-top: 1px solid #f1f5f9;
}

.history-view {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  color: #6d28d9;
  font-weight: 700;
}

.history-pagination {
  margin-top: 20px;
  text-align: center;
}

.history-back {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 0;
  margin-bottom: 18px;
  font-size: 13px;
}

.history-detail-heading {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 18px;
  padding: 16px;
  border-radius: 14px;
  background: #f8fafc;
}

.history-detail-heading > div {
  display: flex;
  flex-direction: column;
  gap: 5px;
}

.history-detail-heading strong {
  color: #4c1d95;
  font-size: 17px;
}

.history-detail-heading span {
  color: #64748b;
  font-size: 12px;
}

.history-readonly {
  padding: 5px 9px;
  border-radius: 999px;
  background: #ede9fe;
  color: #6d28d9 !important;
  font-weight: 700;
  white-space: nowrap;
}

.lesson-prose {
  font-size: 16px;
  line-height: 1.65;
  color: #334155;
  overflow-wrap: break-word;
}

.lesson-prose :deep(h2) {
  margin: 24px 0 12px;
  padding-bottom: 7px;
  border-bottom: 2px solid #6d28d9;
  color: #1e293b;
  font-size: 20px;
  font-weight: 700;
}

.lesson-prose :deep(h3) {
  margin: 18px 0 8px;
  color: #5b21b6;
  font-size: 17px;
  font-weight: 700;
}

.lesson-prose :deep(p),
.lesson-prose :deep(li) {
  margin: 8px 0;
}

.lesson-prose :deep(ul),
.lesson-prose :deep(ol) {
  margin-top: 0;
  margin-bottom: 0;
}

.lesson-prose :deep(ul),
.lesson-prose :deep(ol) {
  padding-left: 24px;
}
</style>
